import { appConfig } from '@/config/app-config'
import type { AppStore } from '@/store/create-store'

import { notificationsMerged } from './store/notifications-slice'
import type { Notification } from './types'

/**
 * The notification service's store: every notification is kept in the browser's localStorage (until the API has an endpoint for them), so
 * it is still there after a refresh, and for each person who signs in on this browser. Read ones are kept as history; only the newest
 * `notificationsLimit` are kept, so it never grows without bound. Another tab's changes are picked up through the `storage` event.
 */
const isNotification = (value: unknown): value is Notification => {
  const item = value as Partial<Notification> | null
  return typeof item?.id === 'string' && typeof item.recipientUserId === 'string' && typeof item.projectId === 'string' && typeof item.createdAt === 'string' && typeof item.title === 'string'
}

export function readNotifications(storage: Storage | null): Notification[] {
  if (!storage) return []
  try {
    const parsed: unknown = JSON.parse(storage.getItem(appConfig.notificationsKey) ?? '[]')
    return Array.isArray(parsed) ? parsed.filter(isNotification) : []
  } catch {
    return []
  }
}

const newestFirst = (a: Notification, b: Notification) => b.createdAt.localeCompare(a.createdAt)

/** Saves the notifications whenever they change. Returns a function that stops. */
export function persistNotifications(store: AppStore, storage: Storage | null): () => void {
  if (!storage) return () => undefined
  let last = store.getState().notifications
  return store.subscribe(() => {
    const current = store.getState().notifications
    if (current === last) return
    last = current
    const newest = Object.values(current.entities).filter((item): item is Notification => Boolean(item)).toSorted(newestFirst).slice(0, appConfig.notificationsLimit)
    try {
      storage.setItem(appConfig.notificationsKey, JSON.stringify(newest))
    } catch {
      // Storage full or unavailable: the notifications are still kept for this visit.
    }
  })
}

/**
 * Picks up what another tab saved: a notification sent to someone using this tab, or one marked read there. Returns a function that
 * stops. The tab that wrote it never gets its own `storage` event, so nothing loops.
 */
export function listenForNotifications(store: AppStore, storage: Storage | null): () => void {
  if (!storage || typeof window === 'undefined') return () => undefined
  const onStorage = (event: StorageEvent) => {
    if (event.key !== appConfig.notificationsKey && event.key !== null) return
    const incoming = readNotifications(storage)
    if (incoming.length) store.dispatch(notificationsMerged(incoming))
  }
  window.addEventListener('storage', onStorage)
  return () => window.removeEventListener('storage', onStorage)
}
